import React, { useEffect, useRef, useState } from 'react';
import { whatKolanutDoesFeatures } from '../engagementContent';

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return reduced;
}

function useInView(threshold = 0.4) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
}

function TypingDots({ dark = false }) {
  return (
    <span className="inline-flex items-center gap-1 h-4" aria-hidden>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={`h-1.5 w-1.5 rounded-full overlay-typing-dot ${
            dark ? 'bg-white/80' : 'bg-gray-400'
          }`}
          style={{ animationDelay: `${i * 160}ms` }}
        />
      ))}
    </span>
  );
}

function bubbleClass(role) {
  return role === 'kolanut'
    ? 'self-end bg-gray-900 text-white rounded-br-md'
    : 'self-start bg-white text-gray-800 border border-gray-200/90 rounded-bl-md';
}

function ChatOverlay({ messages }) {
  const [ref, inView] = useInView();
  const reduced = usePrefersReducedMotion();
  const [visible, setVisible] = useState(reduced ? messages.length : 0);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    if (!inView || reduced || messages.length === 0) {
      if (reduced) setVisible(messages.length);
      return undefined;
    }

    let cancelled = false;
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const loop = async () => {
      while (!cancelled) {
        setVisible(0);
        setTyping(false);
        await wait(400);
        if (cancelled) return;

        for (let i = 0; i < messages.length; i += 1) {
          if (cancelled) return;
          setTyping(true);
          await wait(750);
          if (cancelled) return;
          setTyping(false);
          setVisible(i + 1);
          await wait(1150);
        }

        await wait(2800);
      }
    };

    loop();
    return () => {
      cancelled = true;
    };
  }, [inView, reduced, messages]);

  const start = Math.max(0, visible - 3);
  const shown = messages.slice(start, visible);
  const next = messages[visible];

  return (
    <div
      ref={ref}
      className="absolute inset-0 p-4 sm:p-6 flex flex-col justify-center gap-2.5 pointer-events-none z-10"
    >
      {shown.map((msg, i) => (
        <div
          key={`${start + i}-${msg.text}`}
          className={`overlay-bubble max-w-[88%] sm:max-w-[78%] px-3.5 py-2.5 rounded-xl text-[13px] sm:text-sm leading-snug shadow-sm ${bubbleClass(msg.role)}`}
        >
          {msg.text}
        </div>
      ))}
      {typing && next && (
        <div
          className={`overlay-bubble max-w-[88%] sm:max-w-[78%] px-3.5 py-2.5 rounded-xl shadow-sm ${bubbleClass(next.role)}`}
        >
          <TypingDots dark={next.role === 'kolanut'} />
        </div>
      )}
    </div>
  );
}

function useNotificationStack(pool, maxVisible, inView, reduced, intervalMs = 1700) {
  const seedCount = Math.min(maxVisible, pool.length);
  const [stack, setStack] = useState(() =>
    pool.slice(0, seedCount).map((item, i) => ({ item, id: i }))
  );
  const [enteringId, setEnteringId] = useState(null);
  const nextRef = useRef(seedCount);

  useEffect(() => {
    if (reduced || !inView || pool.length < 2) return undefined;

    const push = () => {
      const item = pool[nextRef.current % pool.length];
      const id = nextRef.current;
      nextRef.current += 1;
      setEnteringId(id);
      setStack((prev) => [{ item, id }, ...prev].slice(0, maxVisible + 1));
    };

    const timer = window.setInterval(push, intervalMs);
    return () => window.clearInterval(timer);
  }, [inView, reduced, pool, maxVisible, intervalMs]);

  return { stack, enteringId };
}

function NotificationList({ stack, enteringId, maxVisible, rowPx, reduced, children }) {
  return (
    <div className="relative overflow-hidden" style={{ height: rowPx * maxVisible }}>
      {stack.map((entry, i) => (
        <div
          key={entry.id}
          className={`absolute inset-x-0 ${i < maxVisible - 1 ? 'border-b border-gray-100' : ''}`}
          style={{
            height: rowPx,
            transform: `translateY(${i * rowPx}px)`,
            zIndex: stack.length - i,
            transition: reduced ? 'none' : 'transform 520ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <div
            className={
              entry.id === enteringId && !reduced ? 'overlay-notify-in h-full' : 'h-full'
            }
          >
            {children(entry.item)}
          </div>
        </div>
      ))}
    </div>
  );
}

function InboxOverlay({ inbox }) {
  const [ref, inView] = useInView();
  const reduced = usePrefersReducedMotion();
  const pool = inbox.rows;
  const maxVisible = Math.min(3, pool.length);
  const { stack, enteringId } = useNotificationStack(pool, maxVisible, inView, reduced, 1700);

  return (
    <div
      ref={ref}
      className="absolute inset-3 sm:inset-5 flex items-center justify-center pointer-events-none z-10"
    >
      <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
          <img src="/kolanut_logo.png" alt="" className="h-4 w-4 object-contain" />
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-gray-400">
            {inbox.title}
          </p>
          <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[#8ae690] overlay-live-dot" aria-hidden />
        </div>
        <NotificationList
          stack={stack}
          enteringId={enteringId}
          maxVisible={maxVisible}
          rowPx={72}
          reduced={reduced}
        >
          {(row) => (
            <div className="flex h-full items-start gap-3 px-4 py-3">
              <span
                className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white ${row.color}`}
              >
                {row.initial}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-sm font-semibold text-gray-900 truncate">{row.name}</p>
                  <span className="text-[11px] text-gray-400 shrink-0">{row.snippet}</span>
                </div>
                <p className="text-xs text-gray-500 truncate mt-0.5">{row.subject}</p>
              </div>
            </div>
          )}
        </NotificationList>
      </div>
    </div>
  );
}

function SummaryOverlay({ summary }) {
  const [ref, inView] = useInView();
  const reduced = usePrefersReducedMotion();
  const pool = summary.lines;
  const maxVisible = Math.min(4, pool.length);
  const { stack, enteringId } = useNotificationStack(pool, maxVisible, inView, reduced, 1600);

  return (
    <div
      ref={ref}
      className="absolute inset-3 sm:inset-5 flex items-end sm:items-center justify-start pointer-events-none z-10"
    >
      <div className="w-full max-w-xs rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-gray-400">
            {summary.title}
          </p>
          <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[#8ae690] overlay-live-dot" aria-hidden />
        </div>
        <NotificationList
          stack={stack}
          enteringId={enteringId}
          maxVisible={maxVisible}
          rowPx={52}
          reduced={reduced}
        >
          {(line) => (
            <div className="flex h-full items-center justify-between gap-3 px-4 py-3">
              <span className="text-sm text-gray-700">{line}</span>
              <span className="h-1.5 w-1.5 rounded-full bg-[#8ae690] overlay-live-dot shrink-0" aria-hidden />
            </div>
          )}
        </NotificationList>
      </div>
    </div>
  );
}

const HEALTH_FRAMES = [
  [14, 7, 9, 5],
  [15, 6, 9, 5],
  [16, 6, 8, 5],
  [15, 7, 8, 6],
  [14, 8, 8, 5],
  [13, 8, 9, 5],
];

function HealthOverlay({ health }) {
  const [ref, inView] = useInView();
  const reduced = usePrefersReducedMotion();
  const [ready, setReady] = useState(reduced);
  const [frame, setFrame] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;
    const id = window.setTimeout(() => setReady(true), reduced ? 0 : 80);
    return () => window.clearTimeout(id);
  }, [inView, reduced]);

  useEffect(() => {
    if (!inView || reduced) return undefined;

    const id = window.setInterval(() => {
      setFrame((current) => (current + 1) % HEALTH_FRAMES.length);
    }, 1800);

    return () => window.clearInterval(id);
  }, [inView, reduced]);

  const counts = HEALTH_FRAMES[frame];
  const total = counts.reduce((sum, n) => sum + n, 0);

  return (
    <div
      ref={ref}
      className="absolute inset-3 sm:inset-5 flex items-center justify-center pointer-events-none z-10"
    >
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white/95 shadow-sm overflow-hidden backdrop-blur-sm">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
          <img src="/kolanut_logo.png" alt="" className="h-4 w-4 object-contain" />
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-gray-400">
            {health.title}
          </p>
          <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[#8ae690] overlay-live-dot" aria-hidden />
        </div>
        <ul className="px-4 py-4 space-y-3.5">
          {health.rows.map((row, i) => {
            const count = counts[i] ?? row.count;
            const pct = total ? Math.round((count / total) * 100) : row.pct;

            return (
              <li key={row.label}>
                <div className="flex items-baseline justify-between gap-3 mb-1.5">
                  <span className="text-sm font-medium text-gray-800">{row.label}</span>
                  <span className="text-sm font-semibold text-gray-900 tabular-nums">{count}</span>
                </div>
                <div className={`h-2 rounded-full overflow-hidden ${row.track}`}>
                  <div
                    className={`h-full rounded-full ${row.bar} overlay-health-bar`}
                    style={{
                      width: ready ? `${pct}%` : '0%',
                      transitionDelay: ready ? `${i * 90}ms` : '0ms',
                    }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

const FEATURE_IMAGE_HEIGHT =
  'h-[17.5rem] sm:h-[22rem] lg:h-[32rem] xl:h-[36rem]';

function FeatureVisual({ feature }) {
  return (
    <div
      className={`relative w-full ${FEATURE_IMAGE_HEIGHT} overflow-hidden rounded-[1.75rem] sm:rounded-[2rem] lg:rounded-[2.25rem] bg-stone-200`}
    >
      <img
        src={feature.imageSrc}
        alt={feature.imageAlt}
        className="absolute inset-0 h-full w-full object-cover saturate-[0.88] contrast-[0.95]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-black/5 to-transparent" aria-hidden />
      <img
        src="/kolanut_logo.png"
        alt=""
        className="absolute bottom-4 left-4 h-6 w-6 object-contain brightness-0 invert opacity-80 z-10"
      />
      {feature.overlayType === 'chat' && <ChatOverlay messages={feature.overlayChat} />}
      {feature.overlayType === 'inbox' && <InboxOverlay inbox={feature.overlayInbox} />}
      {feature.overlayType === 'summary' && <SummaryOverlay summary={feature.overlaySummary} />}
      {feature.overlayType === 'health' && <HealthOverlay health={feature.overlayHealth} />}
      <span className="sr-only">{feature.imageHint}</span>
    </div>
  );
}

function FeatureRow({ feature, reverse }) {
  return (
    <article className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 xl:gap-12 items-center">
      <div className={`w-full min-w-0 ${reverse ? 'lg:col-start-2' : ''}`}>
        <FeatureVisual feature={feature} />
      </div>
      <div
        className={`max-w-md mx-auto w-full text-center lg:max-w-none lg:text-left px-1 ${
          reverse ? 'lg:col-start-1 lg:row-start-1' : ''
        }`}
      >
        <span className="inline-block text-[11px] sm:text-xs font-medium uppercase tracking-[0.16em] text-gray-400">
          {feature.tag}
        </span>
        <h3 className="mt-4 text-2xl sm:text-3xl md:text-[2rem] font-semibold tracking-tight text-gray-900 leading-snug">
          {feature.headline}
        </h3>
        <p className="mt-4 text-base sm:text-lg text-gray-500 leading-relaxed">{feature.body}</p>
      </div>
    </article>
  );
}

const WhatKolanutDoesSection = ({
  features = whatKolanutDoesFeatures,
  title = 'What Kolanut does for you',
  description = 'You need someone on retention. They show up every day. Here is the work your engagement manager owns from day one.',
}) => {
  return (
    <section id="how-it-works" className="py-16 md:py-24 bg-[#f7f8fa] scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="mb-14 md:mb-20 max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-[2.75rem] font-normal tracking-tight text-gray-900 leading-[1.15]">
            {title}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-gray-500 leading-relaxed">
            {description}
          </p>
        </header>

        <div className="space-y-16 md:space-y-24 lg:space-y-28">
          {features.map((feature, i) => (
            <FeatureRow key={feature.tag} feature={feature} reverse={i % 2 === 1} />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes overlay-bubble-in {
          from { opacity: 0; transform: translateY(10px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes overlay-notify-in {
          from { opacity: 0; transform: translateY(-100%); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes overlay-live-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.45; transform: scale(0.82); }
        }
        @keyframes overlay-typing {
          0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
          40% { opacity: 1; transform: translateY(-2px); }
        }
        .overlay-bubble {
          animation: overlay-bubble-in 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .overlay-notify-in {
          animation: overlay-notify-in 520ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .overlay-live-dot {
          animation: overlay-live-pulse 1.6s ease-in-out infinite;
        }
        .overlay-typing-dot {
          animation: overlay-typing 1s ease-in-out infinite;
        }
        .overlay-health-bar {
          transition: width 700ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        @media (prefers-reduced-motion: reduce) {
          .overlay-bubble,
          .overlay-notify-in,
          .overlay-live-dot,
          .overlay-typing-dot {
            animation: none;
          }
          .overlay-health-bar {
            transition: none;
          }
        }
      `}</style>
    </section>
  );
};

export default WhatKolanutDoesSection;
